// ============================================
// FILE: frontend/src/components/HelpSupportScreen.jsx
// ============================================

import React, { useState, useEffect } from 'react';
import { 
  MessageCircle, Phone, Mail, HelpCircle, Search, 
  ChevronRight, ChevronDown, Package, CreditCard, 
  Truck, RefreshCw, Shield, MapPin, Clock, Send,
  CheckCircle, AlertCircle, FileText, Video, BookOpen,
  Settings, Info, Home
} from 'lucide-react';

const HelpSupportScreen = ({ showToast, appSettings, setActiveTab, appTheme = 'blue' }) => {
    // Header style
    const headerStyle = appTheme === 'white'
      ? { background: '#fff', color: '#222', boxShadow: '0 2px 16px 0 rgba(0,0,0,0.04)' }
      : { background: 'var(--primary-gradient)', color: 'var(--text-light)' };
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showContactForm, setShowContactForm] = useState(false);
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  
  // Extract appSettings contact information for easy access
  const [contactInfo, setContactInfo] = useState({
    supportContact: '',
    supportEmail: '',
    hasValidContact: false,
    hasValidEmail: false
  });
  
  // Update contact info when appSettings changes
  useEffect(() => {
    const supportContact = appSettings?.supportContact || '';
    const supportEmail = appSettings?.supportEmail || '';
    const securityAlertsEnabled = appSettings?.enableSecurityAlerts || false;
    
    setContactInfo({
      supportContact,
      supportEmail, 
      hasValidContact: supportContact && supportContact.length > 0,
      hasValidEmail: supportEmail && supportEmail.includes('@')
    });
  }, [appSettings]);

  // FAQ Categories
  const faqCategories = [
    {
      id: 'orders',
      title: 'Orders & Delivery',
      icon: <Package size={24} className="text-blue-600" />,
      questions: [
        {
          id: 1,
          question: 'How do I place an order?',
          answer: 'You can place an order by browsing our products, adding items to your cart, and proceeding to checkout. Select your preferred delivery slot and payment method to complete your order.'
        },
        {
          id: 2,
          question: 'What are the delivery timings?',
          answer: 'We offer three delivery slots: Morning (6 AM - 8 AM), Afternoon (2 PM - 4 PM), and Evening (6 PM - 8 PM). You can select your preferred slot during checkout.'
        },
        {
          id: 3,
          question: 'Can I modify my order after placing it?',
          answer: 'Yes, you can modify your order within 2 hours of placing it. Go to My Orders, select the order, and click "Modify Order". After 2 hours, orders are processed and cannot be changed.'
        },
        {
          id: 4,
          question: 'What if I miss my delivery?',
          answer: 'If you miss your delivery, our delivery partner will attempt redelivery within 2 hours. You can also reschedule delivery from the My Orders section.'
        },
        {
          id: 5,
          question: 'Is there a minimum order value?',
          answer: 'No, there is no minimum order value. However, orders above ₹200 get free delivery, while orders below that have a ₹20 delivery charge.'
        }
      ]
    },
    {
      id: 'payment',
      title: 'Payment & Refunds',
      icon: <CreditCard size={24} className="text-green-600" />,
      questions: [
        {
          id: 6,
          question: 'What payment methods do you accept?',
          answer: 'We accept multiple payment methods: Cash on Delivery (COD), UPI (PhonePe, GPay, Paytm), Credit/Debit Cards, and Digital Wallets (Paytm, Amazon Pay).'
        },
        {
          id: 7,
          question: 'Is my payment information secure?',
          answer: 'Yes, all payment transactions are encrypted and secured using industry-standard SSL technology. We do not store your card details on our servers.'
        },
        {
          id: 8,
          question: 'How do I get a refund?',
          answer: 'Refunds are processed within 5-7 business days to your original payment method. For COD orders, refunds are credited to your wallet or bank account.'
        },
        {
          id: 9,
          question: 'Can I pay later?',
          answer: 'Currently, we only offer COD for deferred payment. Subscription customers can also opt for monthly billing.'
        }
      ]
    },
    {
      id: 'subscription',
      title: 'Subscriptions',
      icon: <RefreshCw size={24} className="text-purple-600" />,
      questions: [
        {
          id: 10,
          question: 'How do subscriptions work?',
          answer: 'Subscriptions provide regular delivery of your favorite products. Choose from Daily, Alternate Days, or Weekly plans and save up to 10% on every delivery.'
        },
        {
          id: 11,
          question: 'Can I pause my subscription?',
          answer: 'Yes, you can pause or modify your subscription anytime from the Profile > Subscriptions section. Paused subscriptions can be resumed whenever you want.'
        },
        {
          id: 12,
          question: 'How do I cancel a subscription?',
          answer: 'Go to Profile > Subscriptions, select the subscription you want to cancel, and click "Cancel Subscription". You will receive deliveries until the end of your current billing period.'
        },
        {
          id: 13,
          question: 'Can I change my subscription plan?',
          answer: 'Yes, you can upgrade or downgrade your subscription plan anytime. The new plan will take effect from your next billing cycle.'
        }
      ]
    },
    {
      id: 'products',
      title: 'Products & Quality',
      icon: <Shield size={24} className="text-orange-600" />,
      questions: [
        {
          id: 14,
          question: 'Are your products fresh?',
          answer: 'Yes, all our products are sourced directly from farms and delivered within 24 hours. We maintain strict quality control to ensure freshness.'
        },
        {
          id: 15,
          question: 'What if I receive a damaged product?',
          answer: 'If you receive a damaged or spoiled product, please contact us immediately with photos. We will provide a full refund or replacement within 24 hours.'
        },
        {
          id: 16,
          question: 'Do you have organic products?',
          answer: 'Yes, we offer a range of organic dairy products. Look for the "Organic" label on product pages.'
        },
        {
          id: 17,
          question: 'What is your return policy?',
          answer: 'Dairy products can be returned within 2 hours of delivery if there are quality issues. Contact support with your order details for immediate assistance.'
        }
      ]
    },
    {
      id: 'account',
      title: 'Account & Security',
      icon: <Shield size={24} className="text-red-600" />,
      questions: [
        {
          id: 18,
          question: 'How do I reset my password?',
          answer: 'Click on "Forgot Password" on the login screen. You will receive an OTP via SMS or WhatsApp to reset your password.'
        },
        {
          id: 19,
          question: 'Can I change my delivery address?',
          answer: 'Yes, you can add or modify delivery addresses from Profile > Addresses. You can also change the address during checkout.'
        },
        {
          id: 20,
          question: 'How do I enable 2FA?',
          answer: 'Go to Profile > Security & Privacy > Two-Factor Authentication and follow the setup instructions. You will need an authenticator app.'
        }
      ]
    }
  ];

  // Quick Help Topics
  const quickHelp = [
    { 
      id: 1, 
      title: 'Track Order', 
      description: 'Check your order status',
      icon: <Truck size={20} />,
      action: 'orders'
    },
    { 
      id: 2, 
      title: 'Change Address', 
      description: 'Update delivery location',
      icon: <MapPin size={20} />,
      action: 'address'
    },
    { 
      id: 3, 
      title: 'Manage Subscription', 
      description: 'Pause or modify plans',
      icon: <RefreshCw size={20} />,
      action: 'subscription'
    },
    { 
      id: 4, 
      title: 'Report Issue', 
      description: 'Quality or delivery problems',
      icon: <AlertCircle size={20} />,
      action: 'report'
    }
  ];

  // Contact Options
  const contactOptions = [
    {
      id: 1,
      title: 'Live Chat',
      description: 'Chat with our support team',
      icon: <MessageCircle size={24} className="text-blue-600" />,
      availability: 'Available 24/7',
      action: () => showToast('Live chat opening...', 'info')
    },
    {
      id: 2,
      title: 'Call Us',
      description: contactInfo.hasValidContact ? contactInfo.supportContact : '+91 88266-00232',
      icon: <Phone size={24} className="text-green-600" />,
      availability: '9 AM - 9 PM (All days)',
      action: () => window.open(`tel:${contactInfo.supportContact.replace(/[^0-9+]/g, '') || '+918826600232'}`)
    },
    {
      id: 3,
      title: 'Email Support',
      description: contactInfo.hasValidEmail ? contactInfo.supportEmail : 'support@dealstore.app',
      icon: <Mail size={24} className="text-purple-600" />,
      availability: 'Response within 24 hours',
      action: () => window.open(`mailto:${contactInfo.hasValidEmail ? contactInfo.supportEmail : 'support@dealstore.app'}`)
    },
    {
      id: 4,
      title: 'WhatsApp',
      description: contactInfo.hasValidContact ? contactInfo.supportContact : '+91 88266-00232',
      icon: <MessageCircle size={24} className="text-green-600" />,
      availability: 'Available 24/7',
      action: () => window.open(`https://wa.me/${contactInfo.supportContact?.replace(/[^0-9]/g, '') || "918826600232"}`)
    },
    {
      id: 5,
      title: 'Corporate Office Address',
      description: 'ALGOLIVE (OPC) PRIVATE LIMITED /n OFFICE 719, 7th floor, ' + '\n' + ' SVH,83 Metrostreet, Sector 83, ' + '\n' + ' Gurugram, Haryana, 122004' + '\n' + 'GSTIN 06AAUCA3418R1ZZ',
      icon: <MapPin size={24} className="text-blue-600" />,
      availability: 'Mon-Fri 9 AM - 6 PM',
      action: () => window.open(' https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3509.51892116558!2d76.97080637528023!3d28.403594875790592!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390d3d760cf2d077%3A0x741b49f09bb5fd3f!2sSVH%2083%20Metro%20Street!5e0!3m2!1sen!2sin!4v1758973129770!5m2!1sen!2sin')
    }
  ];

  const filteredFaqs = faqCategories.map(category => ({
    ...category,
    questions: category.questions.filter(q =>
      q.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      q.answer.toLowerCase().includes(searchQuery.toLowerCase())
    )
  })).filter(category => category.questions.length > 0);

  const toggleFaq = (id) => {
    setExpandedFaq(expandedFaq === id ? null : id);
  };

  const handleSubmitContact = (e) => {
    e.preventDefault();
    showToast(`Thank you for contacting us!\n\nWe'll respond to ${contactForm.email} within 24 hours.`, 'success');
    setContactForm({ name: '', email: '', subject: '', message: '' });
    setShowContactForm(false);
  };

  return (
    <div className="pb-20 min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-6 rounded-b-3xl">
        <div className="flex items-center justify-between mb-2">
          <button
            onClick={() => setActiveTab && setActiveTab('home')}
            className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-all"
            title="Go to Home"
          >
            <Home size={22} />
          </button>
        </div>
        <h1 className="text-2xl font-bold mb-2">Help & Support</h1>
        <p className="text-blue-100">We're here to help you 24/7</p>
      </div>

      <div className="p-4">
        {/* Search Bar */}
        <div className="bg-white rounded-xl shadow-sm p-3 mb-4 flex items-center">
          <Search className="text-gray-400" size={20} />
          <input
            type="text"
            placeholder="Search for help..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="flex-1 ml-3 outline-none text-gray-800"
          />
        </div>

        {/* Quick Help */}
        {!searchQuery && (
          <div className="mb-6">
            <h2 className="text-lg font-bold text-gray-800 mb-3">Quick Help</h2>
            <div className="grid grid-cols-2 gap-3">
              {quickHelp.map(item => (
                <button
                  key={item.id}
                  onClick={() => showToast(`Opening ${item.title}...`, 'info')}
                  className="bg-white rounded-xl shadow-sm p-4 text-left hover:shadow-md transition-all"
                >
                  <div className="bg-blue-50 w-10 h-10 rounded-lg flex items-center justify-center mb-2 text-blue-600">
                    {item.icon}
                  </div>
                  <h3 className="font-semibold text-gray-800 text-sm mb-1">{item.title}</h3>
                  <p className="text-xs text-gray-500">{item.description}</p>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Contact Options */}
        {!searchQuery && (
          <div className="mb-6">
            <h2 className="text-lg font-bold text-gray-800 mb-3">Contact Us</h2>
            
            {/* App Settings Info */}
            {(contactInfo.hasValidContact || contactInfo.hasValidEmail) && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                <div className="flex items-center gap-2 mb-2">
                  <Info size={16} className="text-blue-600" />
                  <span className="text-sm font-medium text-blue-800">App Settings Contact Info</span>
                </div>
                <div className="text-sm text-blue-700 space-y-1">
                  {contactInfo.hasValidContact && (
                    <div className="flex items-center gap-2">
                      <Phone size={14} />
                      <span>Contact: {contactInfo.supportContact}</span>
                    </div>
                  )}
                  {contactInfo.hasValidEmail && (
                    <div className="flex items-center gap-2">
                      <Mail size={14} />
                      <span>Email: {contactInfo.supportEmail}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            <div className="space-y-3">
              {contactOptions.map(option => (
                <button
                  key={option.id}
                  onClick={option.action}
                  className="w-full bg-white rounded-xl shadow-sm p-4 flex items-center hover:shadow-md transition-all"
                >
                  <div className="mr-4">
                    {option.icon}
                  </div>
                  <div className="flex-1 text-left">
                    <h3 className="font-semibold text-gray-800">{option.title}</h3>
                    <p className="text-sm text-gray-600">{option.description}</p>
                    <p className="text-xs text-gray-400 mt-1 flex items-center gap-1">
                      <Clock size={12} />
                      {option.availability}
                    </p>
                  </div>
                  <ChevronRight size={20} className="text-gray-400" />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Contact Form Button */}
        {!searchQuery && !showContactForm && (
          <button
            onClick={() => setShowContactForm(true)}
            className="w-full bg-gradient-to-r from-blue-500 to-blue-600 text-white py-4 rounded-xl font-semibold mb-6 hover:from-blue-600 hover:to-blue-700 transition-all flex items-center justify-center gap-2"
          >
            <Send size={20} />
            Submit a Request
          </button>
        )}

        {/* Contact Form */}
        {showContactForm && (
          <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-800">Submit a Request</h3>
              <button
                onClick={() => setShowContactForm(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            <form onSubmit={handleSubmitContact} className="space-y-3">
              <input
                type="text"
                placeholder="Your Name"
                value={contactForm.name}
                onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                required
                className="w-full border-2 border-gray-200 rounded-lg px-4 py-2 outline-none focus:border-blue-500"
              />
              <input
                type="email"
                placeholder="Your Email"
                value={contactForm.email}
                onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                required
                className="w-full border-2 border-gray-200 rounded-lg px-4 py-2 outline-none focus:border-blue-500"
              />
              <input
                type="text"
                placeholder="Subject"
                value={contactForm.subject}
                onChange={(e) => setContactForm({ ...contactForm, subject: e.target.value })}
                required
                className="w-full border-2 border-gray-200 rounded-lg px-4 py-2 outline-none focus:border-blue-500"
              />
              <textarea
                placeholder="Describe your issue..."
                value={contactForm.message}
                onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                required
                rows="4"
                className="w-full border-2 border-gray-200 rounded-lg px-4 py-2 outline-none focus:border-blue-500 resize-none"
              />
              <button
                type="submit"
                className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition-all"
              >
                Submit Request
              </button>
            </form>
          </div>
        )}

        {/* FAQs */}
        <div className="mb-6">
          <h2 className="text-lg font-bold text-gray-800 mb-3">
            {searchQuery ? 'Search Results' : 'Frequently Asked Questions'}
          </h2>
          
          {filteredFaqs.length === 0 ? (
            <div className="bg-white rounded-xl shadow-sm p-8 text-center">
              <HelpCircle size={48} className="text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No results found for "{searchQuery}"</p>
              <p className="text-sm text-gray-400 mt-2">Try different keywords or contact support</p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredFaqs.map(category => (
                <div key={category.id} className="bg-white rounded-xl shadow-sm overflow-hidden">
                  <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-4 flex items-center gap-3 border-b">
                    {category.icon}
                    <h3 className="font-bold text-gray-800">{category.title}</h3>
                  </div>
                  
                  <div className="divide-y">
                    {category.questions.map(faq => (
                      <div key={faq.id}>
                        <button
                          onClick={() => toggleFaq(faq.id)}
                          className="w-full p-4 flex items-center justify-between text-left hover:bg-gray-50 transition-all"
                        >
                          <span className="font-semibold text-gray-800 pr-4">{faq.question}</span>
                          <ChevronDown 
                            size={20} 
                            className={`text-gray-400 flex-shrink-0 transition-transform ${
                              expandedFaq === faq.id ? 'rotate-180' : ''
                            }`}
                          />
                        </button>
                        
                        {expandedFaq === faq.id && (
                          <div className="px-4 pb-4 text-sm text-gray-600 bg-blue-50">
                            <div className="bg-white rounded-lg p-3 border-l-4 border-blue-500">
                              {faq.answer}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Additional Resources */}
        {!searchQuery && (
          <div className="mb-6">
            <h2 className="text-lg font-bold text-gray-800 mb-3">Additional Resources</h2>
            <div className="space-y-3">
              <button className="w-full bg-white rounded-xl shadow-sm p-4 flex items-center hover:shadow-md transition-all">
                <div className="bg-purple-50 p-3 rounded-lg mr-4">
                  <Video size={24} className="text-purple-600" />
                </div>
                <div className="flex-1 text-left">
                  <h3 className="font-semibold text-gray-800">Video Tutorials</h3>
                  <p className="text-sm text-gray-500">Learn how to use the app</p>
                </div>
                <ChevronRight size={20} className="text-gray-400" />
              </button>

              <button className="w-full bg-white rounded-xl shadow-sm p-4 flex items-center hover:shadow-md transition-all">
                <div className="bg-blue-50 p-3 rounded-lg mr-4">
                  <BookOpen size={24} className="text-blue-600" />
                </div>
                <div className="flex-1 text-left">
                  <h3 className="font-semibold text-gray-800">User Guide</h3>
                  <p className="text-sm text-gray-500">Complete documentation</p>
                </div>
                <ChevronRight size={20} className="text-gray-400" />
              </button>

              <button className="w-full bg-white rounded-xl shadow-sm p-4 flex items-center hover:shadow-md transition-all">
                <div className="bg-green-50 p-3 rounded-lg mr-4">
                  <FileText size={24} className="text-green-600" />
                </div>
                <div className="flex-1 text-left">
                  <h3 className="font-semibold text-gray-800">Terms & Policies</h3>
                  <p className="text-sm text-gray-500">Privacy and terms of service</p>
                </div>
                <ChevronRight size={20} className="text-gray-400" />
              </button>
            </div>
          </div>
        )}

        {/* Still Need Help */}
        <div className="bg-gradient-to-r from-blue-50 to-blue-100 border-2 border-blue-200 rounded-xl p-4 text-center">
          <HelpCircle size={48} className="text-blue-600 mx-auto mb-3" />
          <h3 className="font-bold text-gray-800 mb-2">Still Need Help?</h3>
          <p className="text-sm text-gray-600 mb-4">
            Can't find what you're looking for? Our support team is here to help!
          </p>
          <button
            onClick={() => showToast('Opening live chat...', 'info')}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-all"
          >
            Start Live Chat
          </button>
        </div>
      </div>
    </div>
  );
};

export default HelpSupportScreen;